import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Target, X } from "lucide-react";

const STORAGE_KEY = "kh_cookie_consent_v1";

const DEFAULT_PREFS = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function savePrefs(prefs) {
  const payload = { ...prefs, timestamp: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  // Dispatch event so other parts of app can react (e.g. inject GA/Meta pixel scripts)
  window.dispatchEvent(new CustomEvent("kh:consent-changed", { detail: payload }));
  return payload;
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("banner"); // "banner" | "settings"
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const saved = loadPrefs();
    if (!saved) {
      setOpen(true);
      setMode("banner");
    } else {
      setPrefs(saved);
    }
  }, []);

  // Floating "Cookie-Einstellungen" button visibility
  const [showFab, setShowFab] = useState(false);
  useEffect(() => {
    const saved = loadPrefs();
    setShowFab(!!saved);
  }, [open]);

  const handleAcceptAll = useCallback(() => {
    const next = { necessary: true, analytics: true, marketing: true };
    setPrefs(savePrefs(next));
    setOpen(false);
    setShowFab(true);
  }, []);

  const handleRejectAll = useCallback(() => {
    const next = { necessary: true, analytics: false, marketing: false };
    setPrefs(savePrefs(next));
    setOpen(false);
    setShowFab(true);
  }, []);

  const handleSaveSelection = useCallback(() => {
    savePrefs(prefs);
    setOpen(false);
    setShowFab(true);
  }, [prefs]);

  const openSettings = () => {
    setMode("settings");
    setOpen(true);
  };

  if (!open) {
    return showFab ? (
      <button
        type="button"
        data-testid="cookie-settings-fab"
        onClick={openSettings}
        aria-label="Cookie-Einstellungen ändern"
        className="fixed bottom-5 left-5 z-40 w-11 h-11 flex items-center justify-center bg-black/80 backdrop-blur-md border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#A93043] hover:border-[#A93043] hover:text-white transition-colors shadow-lg"
      >
        <Cookie className="w-4 h-4" />
      </button>
    ) : null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-title"
      data-testid="cookie-consent-modal"
    >
      {/* Backdrop only in settings mode */}
      {mode === "settings" && (
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-auto"
          onClick={() => setOpen(false)}
        />
      )}

      <div
        className={`relative w-full pointer-events-auto bg-[#0a0a0a] border border-[#1f1f29] text-white shadow-2xl
          ${mode === "settings" ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-5xl mx-auto sm:rounded-none"}
        `}
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(123,30,46,0.06) 0%, rgba(0,0,0,0) 60%)",
        }}
      >
        {/* Gold edge */}
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        <div className="p-6 sm:p-8">
          {mode === "settings" && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Schließen"
              data-testid="cookie-close-btn"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/60 hover:text-[#D4AF37] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 shrink-0 items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
              <Cookie className="w-5 h-5" />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="h-px w-6 bg-[#A93043]" />
                <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80">
                  Privacy · DSGVO
                </span>
              </div>
              <h2
                id="cookie-title"
                className="font-display text-2xl sm:text-3xl uppercase text-white leading-tight"
              >
                {mode === "settings" ? "Cookie-Einstellungen" : "Wir respektieren deine Privatsphäre"}
              </h2>
              <p className="mt-3 font-body text-sm text-white/65 max-w-2xl leading-relaxed">
                Wir verwenden Cookies und ähnliche Technologien, um die Website bereitzustellen und – mit
                deiner Einwilligung – Reichweite zu messen und Werbung zu personalisieren. Du kannst deine
                Auswahl jederzeit anpassen. Mehr in unserer{" "}
                <Link
                  to="/datenschutz"
                  onClick={() => setOpen(false)}
                  className="text-[#D4AF37] hover:underline"
                >
                  Datenschutzerklärung
                </Link>
                .
              </p>
            </div>
          </div>

          {/* Settings list */}
          {mode === "settings" && (
            <div className="mt-8 space-y-4">
              <CategoryRow
                icon={<Shield className="w-4 h-4" />}
                title="Notwendig"
                description="Diese Cookies sind technisch erforderlich (z. B. Sprachauswahl, Cookie-Einwilligung). Sie können nicht deaktiviert werden."
                checked
                disabled
                testId="cookie-toggle-necessary"
              />
              <CategoryRow
                icon={<BarChart3 className="w-4 h-4" />}
                title="Statistik & Analyse"
                description="Google Analytics 4 – hilft uns zu verstehen, wie unsere Website genutzt wird (anonymisiert)."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="cookie-toggle-analytics"
              />
              <CategoryRow
                icon={<Target className="w-4 h-4" />}
                title="Marketing & Retargeting"
                description="Meta Pixel & TikTok Pixel – werden für Werbeanzeigen auf Instagram, Facebook und TikTok genutzt."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="cookie-toggle-marketing"
              />
            </div>
          )}

          {/* Actions */}
          <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
            {mode === "banner" ? (
              <>
                <button
                  type="button"
                  data-testid="cookie-settings-btn"
                  onClick={() => setMode("settings")}
                  className="order-3 sm:order-1 font-body text-xs tracking-[0.25em] uppercase text-white/60 hover:text-[#D4AF37] transition-colors px-2 py-3"
                >
                  Einstellungen
                </button>
                <button
                  type="button"
                  data-testid="cookie-reject-btn"
                  onClick={handleRejectAll}
                  className="order-2 px-6 py-3 border border-white/20 text-white font-body text-xs tracking-[0.25em] uppercase hover:border-white/60 transition-colors"
                >
                  Nur notwendige
                </button>
                <button
                  type="button"
                  data-testid="cookie-accept-all-btn"
                  onClick={handleAcceptAll}
                  className="order-1 sm:order-3 px-6 py-3 bg-[#D4AF37] text-black font-body text-xs tracking-[0.25em] uppercase hover:bg-[#A93043] hover:text-white transition-colors"
                >
                  Alle akzeptieren
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="cookie-reject-btn"
                  onClick={handleRejectAll}
                  className="px-5 py-3 border border-white/20 text-white font-body text-xs tracking-[0.25em] uppercase hover:border-white/60 transition-colors"
                >
                  Nur notwendige
                </button>
                <button
                  type="button"
                  data-testid="cookie-save-selection-btn"
                  onClick={handleSaveSelection}
                  className="px-5 py-3 border border-[#D4AF37] text-[#D4AF37] font-body text-xs tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors"
                >
                  Auswahl speichern
                </button>
                <button
                  type="button"
                  data-testid="cookie-accept-all-btn"
                  onClick={handleAcceptAll}
                  className="px-5 py-3 bg-[#D4AF37] text-black font-body text-xs tracking-[0.25em] uppercase hover:bg-[#A93043] hover:text-white transition-colors"
                >
                  Alle akzeptieren
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ icon, title, description, checked, onChange, disabled, testId }) {
  return (
    <div className="border border-[#1f1f29] p-4 flex items-start gap-4 hover:border-[#D4AF37]/40 transition-colors">
      <div className="w-9 h-9 shrink-0 flex items-center justify-center border border-[#1f1f29] text-[#D4AF37]">
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-sm uppercase tracking-wider text-white">
            {title}
          </h3>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={!!checked}
              disabled={disabled}
              onChange={(e) => onChange?.(e.target.checked)}
              data-testid={testId}
            />
            <div
              className={`w-11 h-6 border ${
                disabled
                  ? "bg-[#D4AF37]/30 border-[#D4AF37]/30"
                  : "bg-[#1a1a1a] border-[#2a2a2a] peer-checked:bg-[#D4AF37] peer-checked:border-[#D4AF37]"
              } relative transition-colors`}
            >
              <span
                className={`absolute top-[2px] left-[2px] bg-white w-4 h-4 transition-transform ${
                  checked ? "translate-x-5" : ""
                }`}
              />
            </div>
          </label>
        </div>
        <p className="mt-1.5 font-body text-xs text-white/55 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}
